import type { LayoutWidgetPos } from './infrastructure/contracts/layout.contract';
import type { DashboardUser, ViewLevel } from './dashboard-resolution';
import { viewLevelOf, isDualView } from './dashboard-resolution';
import { dashboardLayoutFor, type DashboardViewLayout } from './dashboard-layouts';
import { ROUTES } from '@/routes/paths';

export interface DashboardDecision {
  kind: 'surface' | 'redirect';
  to?: string;
  level?: ViewLevel;
  layout?: DashboardViewLayout;
  dualView?: boolean;
}

/** Two departments already own a complete dashboard page; they keep it. */
const CANONICAL_REDIRECTS: Record<string, string> = {
  accounts: ROUTES.accountingV2,
  hr: ROUTES.hrDashboard,
};

const singleSurfaceLayout = (id: string, level: ViewLevel, name: string): DashboardViewLayout => {
  const widget: LayoutWidgetPos = { id, x: 0, y: 0, w: 12, h: 12 };
  return { id, level, name, widgets: [widget] };
};

/**
 * Replaces 219 lines of role and username branching with one decision, as data.
 *
 * Note what is GONE: the `bdm1`/`suraj` and `asm1`/`asm2`/`rohit` special cases,
 * the dead `role === 'asm'` test (not a value userSchema.role allows), the regex
 * over the free-text `designation` field, the duplicated IT/marketing branches,
 * and three places where an absent department widened an account's scope.
 */
export function resolveDashboard(
  user: (DashboardUser & { username?: string | null }) | null | undefined,
): DashboardDecision {
  if (!user?.role) return { kind: 'redirect', to: ROUTES.dashboard };
  if (user.role === 'driver') return { kind: 'redirect', to: ROUTES.driverPortal };

  const level = viewLevelOf(user);
  const dept = String(user.department ?? '').trim().toLowerCase();

  // The owner tier is company-wide before any department flavour is considered,
  // which is what the old switchboard did at its very top.
  if (level !== 'company') {
    const redirect = CANONICAL_REDIRECTS[dept];
    if (redirect) return { kind: 'redirect', to: redirect };
  }

  // Field executives work off the road, on their own screen, at every tier.
  if (user.role === 'field_executive') {
    return { kind: 'surface', level, dualView: false, layout: singleSurfaceLayout('field.surface', 'own', 'Field Dashboard') };
  }

  return {
    kind: 'surface',
    level,
    layout: dashboardLayoutFor(level, dept),
    dualView: isDualView(user, level),
  };
}
